import { useCallback, useMemo, useState } from "react";
import { isEqual, noop } from "lodash";
import { useApolloClient } from "@apollo/client";

import Graph_QL, { AnimalInfo, DynamicDataObk, GqlHook_Returns, StateDataObject } from "..";
import { RootState, useAppDispatch, useAppSelector } from "../../../Store";
import { filterByConnection } from "../filterByConnection";
import { BasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { gqlActions } from "../gqlData_Reducer";

// import { newSectionsData } from "../../../Assets/TestData/sectionsData";

import { LfSectionItem } from "./LocationFeedingSlices/lfStructureSlice";
import { CreatePanelLocation, CreatePanelLocationResponse, CreateTerminalLink, MaintainPanelSectionParameters, MaintainPanelSectionResponse, PANEL_CREATE_SECTION, PANEL_MAINTAIN_SECTION, RenameLocation, RenameSection, RenameStation, REORDER_SECTION, ReorderSectionResponse, SectionReorder, UpdatedTerminal, UpdateTerminal } from "./GQLCalls/createLfSectionCall";
import { GET_LF_SECTION_INFO, LocationInfo, SectionInfoResponse, SectionsInfo } from "./GQLCalls/getLfStructureCall";
import { START_NEXT_FEEDING_BY_LOCATION, START_NEXT_FEEDING_BY_SYSTEM, StartNextFeedingByLocation, StartNextFeedingByLocationResponse, StartNextFeedingBySystemResponse } from "./GQLCalls/startNextFeedingCall";
import { FEED_LOCATION, FEED_LOCATION_MULTIPLE, FeedLocationMultiple, FeedLocationMultipleResponse, FeedLocationResponse } from "./GQLCalls/feedLocation";
import { normalizeAnimalCorrection } from "../AnimalDataCalls/useAnimal";
import { SimpleMutationResponse } from "../sharedTypes";
import { DO_EMPTY_FEED_LOCATION, DoEmptyFeedLocation, DoEmptyFeedLocationResponse } from "./GQLCalls/doEmptyFeed";
import { ConfigUpdateValue, DO_CALIBRATION_MULTIPLE, DO_LOCATION_CALIBRATION, DoCalibrationMultiple, DoCalibrationMultipleResponse, DoLocationCalibration, DoLocationCalibrationResponse, UPDATE_CONFIG_FROM_CALIBRATION_LOCATION, UPDATE_CONFIG_FROM_CALIBRATION_MULTIPLE, UpdateConfigFromCalibrationLocation, UpdateConfigFromCalibrationLocationResponse, UpdateConfigFromCalibrationMultipleResponse } from "./GQLCalls/calibrationCalls";
import { batchedMutation } from "../batchedMutation";
import { Def_MutationResponse } from "../UserCalls/GQLCalls/userCalls";
import { upsertAnimals } from "../AnimalDataCalls/AnimalDataSlices/animalsSlice";
import { clearLocationOptions, clearSectionOptions, removeLocationOptions } from "../../../Store/OptionsHandlers/optionsCombinedReducer";
import { DELETE_SECTION_LOCATION, DeleteSectionResponse } from "./GQLCalls/deleteSectionLocation";
import { DELETE_SECTION } from "../DomainCalls/GQLCalls/deleteCalls";
import { selectAllLocations, updateLocations, upsertLocations } from "../SectionsCalls/locationSlice";
import { updateManyStations } from "../StationCalls/StationSlice";
import { dispatchFetchSectionBySystem, removeSectionFromRedux, updateManySections } from "../SectionsCalls/sectionsSlice";
import { FEED_MULTIPLE_BY_TERMINAL, FeedMultipleByTerminal, FeedMultipleByTerminalResponse } from "./GQLCalls/feedByTerminal";

interface LfSectionsHelperFunctions {
  createSectionLocations: (domain: string, sectionName: string, panelSectionType: number, systemId: number, newSection: CreatePanelLocation[], newTerminalLinks?: CreateTerminalLink[]) => Promise<Def_MutationResponse>;
  startNextFeedingByLocation: (locationIds: number[], specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal) => Promise<SimpleMutationResponse[]>
  startFeedingBySystem: (systemId: number) => void;
  feedLocation: (locationid: number, overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons) => Promise<boolean>;
  filteredBySystemId: (domain: string, systemId: number) => SectionsInfo[];
  filteredBySystemIdSectionIndex: (domain: string, systemId: number, SectionIndex: number) => { index: number, name: string, pens: LocationInfo[] };
  getPensFromSectionIdPenIndex: (domain: string, systemId: number, SectionIndex: number, LocationIndex: number) => { index: number, name: string, animals: AnimalInfo[] };
  feedLocationMultiple: (locationIds: number[], overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal) => Promise<SimpleMutationResponse[]>;
  doEmptyFeedLocation: (locationIds: number[], triggerAmount: number, pauseTime: number, amount: number, specific?: Graph_QL.GQLCons | undefined, abortSignal?: AbortSignal) => Promise<SimpleMutationResponse[]>
  doLocationCalibration: (locationIds: number[], triggerAmount: number, pauseTime: number, calibrationAmount: number, specific?: Graph_QL.GQLCons | undefined, abortSignal?: AbortSignal) => Promise<SimpleMutationResponse[]>
  updateConfigFromCalibrationLocation: (updateValues: ConfigUpdateValue[], specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal) => Promise<SimpleMutationResponse[]>
  maintainSectionLocations: (sectionRenames: RenameSection[], locationRenames: RenameLocation[], stationRenames: RenameStation[], terminalChanges: UpdatedTerminal[], specific?: Graph_QL.GQLCons) => Promise<{ success: boolean, errorColumns?: { key: string, value: string }[] }>;
  deleteSectionLocations: (sectionId: number) => Promise<boolean>;
}

interface SectionInfoInput extends BasicQueryInfoInput {
  idIdentifier?: string;
}

export const useSection = () => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();

  const domain = useAppSelector((state: RootState) => state.activeData?.farm);

  // #region | reorderSectionLocations
  const reorderSectionLocations = useCallback(async (sectionReorders: SectionReorder[]) => {
    try {
      const result = await client.mutate<ReorderSectionResponse>({
        mutation: REORDER_SECTION,
        variables: { domain, sectionReorders }
      })
      if (!result.data?.section.reorder.success) {
        return false;
      }
      const reorderedSections = result.data?.section.reorder.updatedValues
      if (reorderedSections?.length) {
        const changes = reorderedSections.map((section) => ({ id: section.section_id, changes: section }));
        dispatch(updateManySections(changes))
        dispatch({ type: String(gqlActions.domain.lfSections.upsert), payload: { con: { domain }, data: reorderedSections } });
      }
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }, [client, dispatch, domain])
  // #endregion

  // #region | feedMultipleByTerminal
  const feedMultipleByTerminal = useCallback(async (terminalIds: number[], overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
    if (domain === undefined) {
      return []
    }
    try {
      return await batchedMutation<FeedMultipleByTerminalResponse, FeedMultipleByTerminal>(
        client,
        {
          mutation: FEED_MULTIPLE_BY_TERMINAL,
          variables: { domain, terminalIds, overwriteFeedcurve, amount },
          responsePath: (data) => data?.terminal.feedMultiple,
          ids: terminalIds,
        },
        abortSignal
      );
    } catch (error) {
      // return {result: data, error?: string messeage/ object, loading}
      console.log(error)
      return []
    }
  },
    [client]
  );
  // #endregion

  // #region | calibrateMultipleByTerminal
  const calibrateMultipleByTerminal = useCallback(async (terminalIds: number[], triggerAmount: number, pauseTime: number, calibrationAmount: number, specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
    if (domain === undefined) {
      return []
    }
    try {
      return await batchedMutation<DoCalibrationMultipleResponse, DoCalibrationMultiple>(
        client,
        {
          mutation: DO_CALIBRATION_MULTIPLE,
          variables: { domain, terminalIds, triggerAmount, pauseTime, calibrationAmount },
          responsePath: (data) => data?.terminal.doCalibrationMultiple,
          ids: terminalIds
        },
        abortSignal
      );
    } catch (error) {
      console.log(error)
      return []
    }
  },
    [client]
  );
  // #endregion

  // #region | updateConfigFromCalibrationMultiple
  const updateConfigFromCalibrationMultiple = useCallback(async (updateValues: ConfigUpdateValue[], specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
    if (domain === undefined) {
      return []
    }
    try {
      return await batchedMutation<UpdateConfigFromCalibrationMultipleResponse, UpdateConfigFromCalibrationLocation>(
        client,
        {
          mutation: UPDATE_CONFIG_FROM_CALIBRATION_MULTIPLE,
          variables: { domain, updateValues },
          responsePath: (data) => data?.terminal.updateConfigFromCalibrationMultiple,
          ids: updateValues.map(e => e.id)
        },
        abortSignal
      );
    } catch (error) {
      console.log(error)
      return []
    }
  },
    [client]
  );
  // #endregion

  return { reorderSectionLocations, feedMultipleByTerminal, calibrateMultipleByTerminal, updateConfigFromCalibrationMultiple }
}

/**
 * @deprecated
 * @param connection 
 * @returns 
 */
export const useOldSection = (connection: Graph_QL.GQLCons): GqlHook_Returns<LfSectionItem, SectionInfoInput> & LfSectionsHelperFunctions => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const [, setCursorId] = useState(-1);

  const lfSectionsState = useAppSelector(({ graphQlData: { domain } }) => domain.lfSections);
  const locations = useAppSelector(selectAllLocations);
  const { fullState } = useMemo(() => {
    // console.log("State changed", ); TODO: State check
    const state = filterByConnection(lfSectionsState, Graph_QL.ToArr(connection)) as Record<string, LfSectionItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = { ...data.data, domain: key };
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    }, { fullState: state, data: [] } as StateDataObject<LfSectionItem>)
  }, [lfSectionsState]);

  const filteredBySystemId = (domain: string, systemId: number) => {
    let sections = fullState[domain] ? fullState[domain].data.filter(s => s.system_id === systemId) : []
    return sections;
  }

  const filteredBySystemIdSectionIndex = (domain: string, systemId: number, SectionIndex?: number) => {
    let sections = filteredBySystemId(domain, systemId)

    let SectionIndexed = sections.findIndex(s => s.section_id === SectionIndex);
    let SectionName = sections[SectionIndexed]?.name
    const sectionId = sections[SectionIndexed]?.section_id;
    const SectionPens = locations.filter((location) => location.sectionId === sectionId) ?? [];
    return { index: SectionIndexed, name: SectionName, pens: SectionPens }
  }

  const getPensFromSectionIdPenIndex = (domain: string, systemId: number, SectionIndex?: number, LocationIndex?: number) => {
    let SectionLocations = filteredBySystemIdSectionIndex(domain, systemId, SectionIndex).pens;

    let locationIndexed = SectionLocations.findIndex(l => l.location_id === LocationIndex);
    let locationName = SectionLocations[locationIndexed]?.name;
    let locationAnimals = SectionLocations[locationIndexed]?.animals ?? [];
    return { index: locationIndexed, name: locationName, animals: locationAnimals }
  }

  const getLfSectionInfo = useCallback(async (input: SectionInfoInput, specific?: Graph_QL.GQLCons,) => {
    const usedConnection = specific?.connection ? specific.connection : connection.connection;
    const { idIdentifier, ...remainingInput } = input;
    const usedInput = remainingInput ? remainingInput : { fetchAmount: 500 };

    try {
      await client.query<SectionInfoResponse>({ query: GET_LF_SECTION_INFO, variables: { ...usedConnection, input: usedInput, idIdentifier, ifFeedCurveBaseInfoOnly: true, ifFeedCurveInfo: false, ifSpreading: false, ifSkipConsumption: false, ifSkipLastTenDaysConsumption: false }, fetchPolicy: "network-only" })// input.ids && input.ids?.length > 0 ? "network-only" : undefined}) // TODO CTO shouldn't always be network only
        .then((e) => {
          if (e && e.data) {
            let result_data: SectionsInfo[] = e.data.section.info.map((section) => ({ ...section, section_locations: section.section_locations.map((location) => ({ ...location, animals: location.animals.map(normalizeAnimalCorrection) })) }));
            dispatch({ type: String(gqlActions.domain.lfSections.maintained), payload: { con: usedConnection, data: result_data } });
            const animalsInResult = result_data.flatMap((section) => section.section_locations.flatMap((location) => location.animals));
            if (animalsInResult.length) {
              dispatch(upsertAnimals(animalsInResult));
            }
            setCursorId(result_data.at(-1)?.section_id ?? -1)
            const locationsInResult = result_data.flatMap((section) => section.section_locations.map((location) => ({ ...location, systemId: section.system_id, sectionId: section.section_id })))
            if (locationsInResult.length) {
              dispatch(upsertLocations(locationsInResult));
            }
          }
        }).catch((error) => { console.log(error) })
    } catch (error) {
      console.log(error);
    }
  }, [])

  const createSectionLocations = async (domain: string, sectionName: string, panelSectionType: number, systemId: number, newSection: CreatePanelLocation[], newTerminalLinks?: CreateTerminalLink[]) => {
    const errorResponse = { success: false, code: "400", message: "Failed to create section" }
    try {
      const result = await client.mutate<CreatePanelLocationResponse>({ mutation: PANEL_CREATE_SECTION, variables: { domain, sectionName, panelSectionType, systemId, section: newSection, terminalLinks: newTerminalLinks } })
      if (result.data?.section.create.success) {
        getLfSectionInfo({ ids: result.data.section.create.updatedValues, idIdentifier: "section_id" }, connection)
        dispatch(dispatchFetchSectionBySystem(systemId, true))
        dispatch(clearSectionOptions());
        dispatch(clearLocationOptions());
      }
      if (result.data?.section.create) {
        return result.data?.section.create;
      }
      return errorResponse;
    } catch (error) {
      return errorResponse;
    }
  }

  const maintainSectionLocations = useCallback(async (sectionRenames: RenameSection[], locationRenames: RenameLocation[], stationRenames: RenameStation[], terminalChanges: UpdatedTerminal[], specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific ? specific.connection : connection.connection;
    try {
      return client
        .mutate<MaintainPanelSectionResponse>({
          mutation: PANEL_MAINTAIN_SECTION,
          variables: { ...usedConnection, sectionRenames, locationRenames, stationRenames, terminalChanges }
        })
        .then((e) => {
          if (!e.data || !e.data.section.maintain.success) {
            return { success: false, errorColumns: e.data?.section.maintain.errorColumns };
          }
          const { updatedTerminals, updatedSections, updatedLocations, updatedStations } = e.data?.section.maintain.updatedValues;

          if (updatedTerminals.length) {
            dispatch({ type: String(gqlActions.domain.terminals.maintained), payload: { con: connection?.connection, data: updatedTerminals } });
          }

          if (updatedLocations.length) {
            dispatch(updateLocations(updatedLocations.map((location) => ({ id: location.location_id, changes: location }))))
          }
          if (updatedSections.length) {
            dispatch({ type: String(gqlActions.domain.lfSections.upsert), payload: { con: usedConnection, data: updatedSections } });
          }
          if (updatedStations.length) {
            dispatch(updateManyStations(updatedStations.map((station) => ({ id: station.station_id, changes: station }))))
          }
          return { success: true };
        })
        .catch((error) => {
          console.log(error);
          return { success: false };
        });
    } catch (error) {
      console.log(error);
      return { success: false };
    }
  }, [client, connection.connection, dispatch])



  const deleteSectionLocations = useCallback(async (sectionId: number) => {
    try {
      const result = await client.mutate<DeleteSectionResponse>({ mutation: DELETE_SECTION_LOCATION, variables: { ...connection.connection, sectionId } })
      if (result.data === null || result.data === undefined) {
        return false;
      }
      const { success, deletionResults } = result.data.section.delete;

      if (!success) {
        return false;
      }

      dispatch(removeSectionFromRedux(sectionId))
      if (deletionResults.deletedLocations.length) {
        dispatch(removeLocationOptions(deletionResults.deletedLocations))
      }
      if (deletionResults.deletedTerminals.length) {
        dispatch({ type: String(gqlActions.domain.terminals.remove), payload: { con: connection.connection, data: deletionResults.deletedTerminals } })
      }
      dispatch({ type: String(gqlActions.domain.lfSections.remove), payload: { con: connection.connection, data: [sectionId] } })
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }, [client, connection.connection, dispatch])

  const startNextFeedingByLocation = useCallback(
    async (locationIds: number[], specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
      const usedConnection = specific ? specific.connection : connection.connection;
      const { domain } = usedConnection as { domain: string };
      return await batchedMutation<StartNextFeedingByLocationResponse, StartNextFeedingByLocation>(
        client,
        {
          mutation: START_NEXT_FEEDING_BY_LOCATION,
          variables: { domain, locationids: locationIds },
          responsePath: (data) => data?.location.startNextFeeding,
          ids: locationIds,
        },
        abortSignal
      );
    },
    [client, connection.connection]
  );

  /**
   * @deprecated needs to return which systems succeded
   */
  const startFeedingBySystem = async (systemId: number) => {
    try {
      client.mutate<StartNextFeedingBySystemResponse>({ mutation: START_NEXT_FEEDING_BY_SYSTEM, variables: { ...connection.connection, feedCurveId: 0, systemId } })
        .then((result) => {
          console.log(result);
        })
    } catch (error) {
      console.log(error);
    }
  }

  const feedLocation = useCallback(async (locationId: number, overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific ? specific.connection : connection.connection
    try {
      return client.mutate<FeedLocationResponse>({ mutation: FEED_LOCATION, variables: { ...usedConnection, locationid: locationId, overwriteFeedcurve, amount } })
        .then((e) => {
          const feedLocation = e.data?.location.feed;
          if (feedLocation) {
            return feedLocation.success ? true : false;
          }
          return false;
        }).catch((error) => { return false })
    } catch (error) { console.log(error); return false }
  }, [client, connection.connection])

  const feedLocationMultiple = useCallback(
    async (locationIds: number[], overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
      const usedConnection = specific ? specific.connection : connection.connection;
      const { domain } = usedConnection as { domain: string };
      return await batchedMutation<FeedLocationMultipleResponse, FeedLocationMultiple>(
        client,
        {
          mutation: FEED_LOCATION_MULTIPLE,
          variables: { domain, locationIds, overwriteFeedcurve, amount },
          responsePath: (data) => data?.location.feedMultiple,
          ids: locationIds,
        },
        abortSignal
      );
    },
    [client, connection.connection]
  );

  const doEmptyFeedLocation = useCallback(
    async (locationIds: number[], triggerAmount: number, pauseTime: number, amount: number, specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
      const usedConnection = specific ? specific.connection : connection.connection;
      const { domain } = usedConnection as { domain: string };
      return await batchedMutation<DoEmptyFeedLocationResponse, DoEmptyFeedLocation>(
        client,
        {
          mutation: DO_EMPTY_FEED_LOCATION,
          variables: { domain, locationIds, triggerAmount, pauseTime, amount },
          responsePath: (data) => data?.location.doEmptyFeed,
          ids: locationIds
        },
        abortSignal
      );
    },
    [client, connection.connection]
  );

  const doLocationCalibration = useCallback(
    async (locationIds: number[], triggerAmount: number, pauseTime: number, calibrationAmount: number, specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
      const usedConnection = specific ? specific.connection : connection.connection;
      const { domain } = usedConnection as { domain: string };
      return await batchedMutation<DoLocationCalibrationResponse, DoLocationCalibration>(
        client,
        {
          mutation: DO_LOCATION_CALIBRATION,
          variables: { domain, locationIds, triggerAmount, pauseTime, calibrationAmount },
          responsePath: (data) => data?.location.doCalibration,
          ids: locationIds
        },
        abortSignal
      );
    },
    [client, connection.connection]
  );

  const updateConfigFromCalibrationLocation = useCallback(
    async (updateValues: ConfigUpdateValue[], specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
      const usedConnection = specific ? specific.connection : connection.connection;
      const { domain } = usedConnection as { domain: string };
      return await batchedMutation<UpdateConfigFromCalibrationLocationResponse, UpdateConfigFromCalibrationLocation>(
        client,
        {
          mutation: UPDATE_CONFIG_FROM_CALIBRATION_LOCATION,
          variables: { domain, updateValues },
          responsePath: (data) => data?.location.updateConfigFromCalibration,
          ids: updateValues.map(e => e.id)
        },
        abortSignal
      );
    },
    [client, connection.connection]
  );

  return { call: getLfSectionInfo, fetchMore: noop, deleteEntries: noop, data: fullState, filteredBySystemId, filteredBySystemIdSectionIndex, getPensFromSectionIdPenIndex, maintainSectionLocations, maintainEntries: noop, createSectionLocations, startNextFeedingByLocation, startFeedingBySystem, feedLocation, feedLocationMultiple, doEmptyFeedLocation, doLocationCalibration, updateConfigFromCalibrationLocation, deleteSectionLocations }
} 

